import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  View,
  useWindowDimensions,
} from "@-/com.npmjs.react-native";
import Animated, {
  createAnimatedComponent,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import {
  LiquidGlassView as LiquidGlassViewFromLiquidGlass,
  isLiquidGlassSupported,
} from "@-/com.npmjs.callstack__liquid-glass";
import { cn } from "@-/com.npmjs.cn";
import { useUniwind, withUniwind } from "@-/com.npmjs.uniwind";
import {
  MenuPopoverSurface,
  NATIVE_MENU_CORNER_RADIUS,
} from "@-/dev.improve.haniverse.components.ui.menu-popover-surface";
import { FrostedBackdropOverlay } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import {
  useToolbarGlassTint,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";

const ToolbarPopoverTopContext = createContext<number | null>(null);

const AnimatedLiquidGlassView = createAnimatedComponent(
  withUniwind(LiquidGlassViewFromLiquidGlass),
);

/** The glass leaves its button the way a native menu does: one soft overshoot, then rest. */
const OPEN_SPRING = { dampingRatio: 0.8, duration: 300 } as const;
/** Collapsing returns into the button without overshooting past it. */
const CLOSE_SPRING = { dampingRatio: 1, duration: 220 } as const;

/**
 * How long the collapse is drawn after the modal has gone. A spring's `duration` is perceptual:
 * Reanimated runs it 1.5 times as long. This timer only stops drawing the collapse and shows the
 * trigger again. Nothing waits for it: the action ran on the press, touches already pass through,
 * and a press brings the menu back at any point.
 */
const HAND_BACK_MS = CLOSE_SPRING.duration * 1.5;

/** The modal's surface is laid out in window coordinates. */
const WINDOW_ORIGIN = { x: 0, y: 0 } as const;

/**
 * How long the glass takes to shed the button's tint for the menu material, and to take it back.
 * Close to the morph's perceptible motion, so the material settles as the shape does.
 */
const GLASS_EXPAND_MS = 320;
const GLASS_COLLAPSE_MS = 240;

interface PopoverControls {
  /**
   * Collapses the popover. Call it in the same handler as the action, never defer the action
   * until the popover has closed: the modal and its content go in the same commit, so a surface
   * the action presents is never behind the menu, and nothing waits for the collapse.
   */
  close: () => void;
  isOpen: boolean;
  onContentSizeChange: (width: number, height: number) => void;
}

interface ToolbarPopoverFrame {
  height: number;
  width: number;
  x: number;
  y: number;
}

/** The glass's morph between the trigger and the menu, driven by `progress` (0 → 1). */
interface ToolbarPopoverMorph {
  /** The trigger's frame in the window: where the glass starts and where it hands back. */
  anchor: ToolbarPopoverFrame;
  expandedCornerRadius: number;
  /** The menu's frame in the window. */
  frame: ToolbarPopoverFrame;
  progress: SharedValue<number>;
}

/**
 * Shared modal geometry and motion for More, account, and toolbar option menus, and for the Debug
 * and Admin panels that grow out of a toolbar button on a swipe down.
 *
 * The surface IS the button's glass, grown: it starts at the trigger's frame with the trigger's
 * material and round corners, so the handoff from the trigger (hidden underneath) is invisible,
 * then stretches into the menu while UIKit drops the button's tint: the menu is the system menu's
 * material, an untinted regular glass that UIKit itself thickens at that size.
 *
 * The modal exists only while the menu is open. Closing hides it in the same commit, so the
 * closing menu never blocks a touch and never sits above a sheet, dialog or toast that the
 * pressed action presents. The collapse is then drawn in place at the trigger, below every such
 * surface: the same morph backwards, without the menu's content, taking the button's tint back
 * before handing back. It takes no touches, and a press on the trigger meanwhile turns
 * it around into the menu again. A fixed timer ends the drawing; nothing waits for the
 * animation.
 */
export function ToolbarExpandingPopover({
  backdropTestID,
  children,
  collapsedContent,
  dismissLabel,
  fill = false,
  fixedHeight,
  headerOutset = 0,
  onOpenChange,
  testID,
  trigger,
}: {
  backdropTestID?: string;
  children: (controls: PopoverControls) => ReactNode;
  /** The button's glyph/avatar, carried by the expanding surface during the handoff. */
  collapsedContent?: ReactNode;
  dismissLabel: string;
  /** Grow from the trigger's row down to the bottom safe edge, whatever the content's height. */
  fill?: boolean;
  fixedHeight?: number;
  /** Extra surface space above and beside the header controls. */
  headerOutset?: number;
  onOpenChange?: (isOpen: boolean) => void;
  testID?: string;
  trigger: (controls: { isOpen: boolean; open: () => void }) => ReactNode;
}) {
  const { diameter } = useToolbarGridMetrics();
  const parentTop = useContext(ToolbarPopoverTopContext);
  const insets = useSafeAreaInsets();
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const [isOpen, setIsOpen] = useState(false);
  // The collapse still being drawn at the trigger, a new one per close; `null` once handed back.
  const [handBack, setHandBack] = useState<{ isGlassExpanded: boolean } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);
  const [isModalReady, setIsModalReady] = useState(Platform.OS === "web");
  // The glass first shows with the button's material; it becomes the menu's only once on screen.
  const [isGlassExpanded, setIsGlassExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [anchor, setAnchor] = useState({
    height: diameter,
    width: diameter,
    x: insets.left + 9,
    y: insets.top,
  });
  const anchorRef = useRef<View>(null);
  const progress = useSharedValue(0);
  const hasTakenOverTrigger = isOpen && isPositioned && isModalReady;
  // Until the modal's surface has taken over again, a re-opened menu keeps collapsing here.
  const isHandingBack = handBack !== null && !hasTakenOverTrigger;
  // Include the drawer's lane. The modal covers it without changing the toolbar underneath.
  const left = insets.left + 9 - headerOutset;
  const width = Math.max(diameter, windowWidth - left - insets.right - 9 + headerOutset);
  const anchorTop = (parentTop ?? anchor.y) - headerOutset;
  const bottom = windowHeight - insets.bottom - 16;
  const height = Math.min(
    fill
      ? Math.max(diameter, bottom - anchorTop)
      : (fixedHeight ?? Math.max(diameter, contentHeight + 2)),
    Math.max(diameter, windowHeight - insets.top - insets.bottom - 16 + headerOutset),
  );
  const top = Math.max(insets.top - headerOutset, Math.min(anchorTop, bottom - height));
  // Exception to `rounded-xs`: the menu is its circular button's glass grown into a system menu, so
  // on iOS Liquid Glass it ends at the system menu platter's 32pt corners. Elsewhere it keeps the
  // toolbar buttons' round corners, concentric with the drawer and menu buttons, which sit
  // `headerOutset` inside its top corners.
  const expandedCornerRadius = isLiquidGlassSupported
    ? NATIVE_MENU_CORNER_RADIUS
    : diameter / 2 + headerOutset;

  const open = () => {
    if (isOpen) {
      return;
    }
    if (handBack === null) {
      progress.set(0);
      setContentHeight(0);
    }
    // Otherwise the collapse turns around where it is, into a menu of the size it had.
    setIsPositioned(false);
    setIsModalReady(Platform.OS === "web");
    setIsGlassExpanded(false);
    anchorRef.current?.measureInWindow((x, y, measuredWidth, measuredHeight) => {
      if (measuredWidth > 0 && measuredHeight > 0) {
        setAnchor({ height: measuredHeight, width: measuredWidth, x, y });
        setIsPositioned(true);
      }
    });
    setIsOpen(true);
    onOpenChange?.(true);
  };
  const close = () => {
    if (!isOpen) {
      return;
    }
    setIsOpen(false);
    // The drawing starts as the glass was: a menu that never became one hands back as the button.
    setHandBack({ isGlassExpanded });
    progress.set(withSpring(0, CLOSE_SPRING));
    onOpenChange?.(false);
  };

  useEffect(() => {
    if (isOpen && hasTakenOverTrigger && (fill || fixedHeight !== undefined || contentHeight > 0)) {
      // The surface has shown with the trigger's glass by now, so the material change animates.
      setIsGlassExpanded(true);
      progress.set(withSpring(1, OPEN_SPRING));
    }
  }, [contentHeight, fill, fixedHeight, hasTakenOverTrigger, isOpen, progress]);

  useEffect(() => {
    if (handBack === null) {
      return;
    }
    // Cleanup only: stop drawing the collapse once the spring has run its course.
    const timeout = setTimeout(setHandBack, HAND_BACK_MS, null);
    return () => clearTimeout(timeout);
  }, [handBack]);

  const morph: ToolbarPopoverMorph = {
    anchor,
    expandedCornerRadius,
    frame: { height, width, x: left, y: top },
    progress,
  };
  const contentStyle = useAnimatedStyle(() => ({
    opacity: Math.min(1, Math.max(0, (progress.get() - 0.35) / 0.65)),
  }));

  return (
    <View className="shrink-0" collapsable={false} ref={anchorRef}>
      {/* The glass stands in for the trigger while the menu is open and while it collapses back.
          Keep the trigger mounted for measurement, but never paint it behind the glass. */}
      <View
        accessibilityElementsHidden={hasTakenOverTrigger}
        className={hasTakenOverTrigger || isHandingBack ? "opacity-0" : undefined}
        importantForAccessibility={hasTakenOverTrigger ? "no-hide-descendants" : "auto"}
      >
        {trigger({ isOpen, open })}
      </View>
      {isHandingBack ? (
        <>
          <ToolbarPopoverHandBack
            collapsedContent={collapsedContent}
            isGlassExpanded={handBack.isGlassExpanded}
            morph={morph}
          />
          {/* iOS hit-testing skips an invisible view, so the hidden trigger takes no press until
              it shows again: this stands in for it, and turns the collapse back into the menu. */}
          <Pressable
            accessibilityElementsHidden
            className="absolute inset-0"
            hitSlop={8}
            importantForAccessibility="no-hide-descendants"
            onPress={open}
            testID={testID === undefined ? undefined : `${testID}-hand-back`}
          />
        </>
      ) : null}
      <Modal
        animationType="none"
        onRequestClose={() => close()}
        onShow={() => setIsModalReady(true)}
        statusBarTranslucent
        supportedOrientations={["portrait", "landscape"]}
        transparent
        visible={isOpen}
      >
        <FrostedBackdropOverlay>
          <KeyboardAvoidingView
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            className="flex-1"
          >
            <Pressable
              accessibilityLabel={dismissLabel}
              accessibilityRole="button"
              className="absolute inset-0"
              onPress={() => close()}
              testID={backdropTestID ?? (testID === undefined ? undefined : `${testID}-backdrop`)}
            />
            <ToolbarPopoverMorphSurface
              accessibilityViewIsModal
              className={hasTakenOverTrigger ? "opacity-100" : "opacity-0"}
              collapsedContent={collapsedContent}
              glassExpanded={isGlassExpanded}
              morph={morph}
              origin={WINDOW_ORIGIN}
              testID={testID}
            >
              {/* Laid out at the final size, 1pt in from each edge, so nothing reflows. */}
              <Animated.View
                className="absolute top-px left-px"
                style={[{ height: height - 2, width: width - 2 }, contentStyle]}
              >
                <ToolbarPopoverTopContext value={top}>
                  {children({
                    close,
                    isOpen,
                    onContentSizeChange: (_width, measuredHeight) =>
                      setContentHeight(measuredHeight),
                  })}
                </ToolbarPopoverTopContext>
              </Animated.View>
            </ToolbarPopoverMorphSurface>
          </KeyboardAvoidingView>
        </FrostedBackdropOverlay>
      </Modal>
    </View>
  );
}

/**
 * The glass at `progress` between the trigger's frame and the menu's, positioned against its
 * parent's window position (`origin`). `children` is the menu's content, clipped to the corners.
 */
function ToolbarPopoverMorphSurface({
  children,
  className,
  collapsedContent,
  glassExpanded,
  morph: { anchor, expandedCornerRadius, frame, progress },
  origin,
  ...props
}: Omit<ComponentProps<typeof Animated.View>, "children" | "style"> & {
  children?: ReactNode;
  collapsedContent?: ReactNode;
  glassExpanded: boolean;
  morph: ToolbarPopoverMorph;
  /** The window position of the surface's parent, which its frame is laid out against. */
  origin: { x: number; y: number };
}) {
  const surfaceStyle = useAnimatedStyle(() => {
    const p = progress.get();
    return {
      height: anchor.height + (frame.height - anchor.height) * p,
      left: anchor.x - origin.x + (frame.x - anchor.x) * p,
      top: anchor.y - origin.y + (frame.y - anchor.y) * p,
      width: anchor.width + (frame.width - anchor.width) * p,
    };
  });
  const collapsedContentStyle = useAnimatedStyle(() => ({
    height: anchor.height,
    left: (anchor.x - frame.x) * progress.get(),
    opacity: Math.max(0, 1 - progress.get() / 0.45),
    top: (anchor.y - frame.y) * progress.get(),
    width: anchor.width,
  }));
  const cornerStyle = useAnimatedStyle(() => {
    const p = progress.get();
    const radius = anchor.height / 2 + (expandedCornerRadius - anchor.height / 2) * p;
    // Never past a capsule: a menu of just its header band is no taller than the corners.
    const shortSide = Math.min(
      anchor.height + (frame.height - anchor.height) * p,
      anchor.width + (frame.width - anchor.width) * p,
    );
    return { borderRadius: Math.max(0, Math.min(radius, shortSide / 2)) };
  });

  return (
    // No clipping here: the glass draws its own shadow outside the surface.
    <Animated.View
      {...props}
      className={cn("absolute", !isLiquidGlassSupported && "shadow-lg shadow-black/15", className)}
      style={surfaceStyle}
    >
      <ToolbarPopoverSurface expanded={glassExpanded} style={cornerStyle} />
      <Animated.View className="absolute inset-0 overflow-hidden" style={cornerStyle}>
        <Animated.View
          accessibilityElementsHidden
          className="absolute items-center justify-center overflow-hidden rounded-full"
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
          style={collapsedContentStyle}
        >
          {collapsedContent}
        </Animated.View>
        {children}
      </Animated.View>
    </Animated.View>
  );
}

/**
 * The collapse, drawn in place at the trigger once the modal has gone: in the toolbar's own layer,
 * so a sheet, dialog or toast the closing action presents is above it. It is only a drawing — no
 * content, no touches, no accessibility — and the timer that unmounts it is not awaited by
 * anything.
 */
function ToolbarPopoverHandBack({
  collapsedContent,
  isGlassExpanded: wasGlassExpanded,
  morph,
}: {
  collapsedContent?: ReactNode;
  /** Whether the glass had become the menu's, so this one starts with the same material. */
  isGlassExpanded: boolean;
  morph: ToolbarPopoverMorph;
}) {
  // A glass shows first with the tint it mounts with, so this one appears as the menu's and takes
  // the button's tint from the next frame, as the menu's own glass would have.
  const [isGlassExpanded, setIsGlassExpanded] = useState(wasGlassExpanded);
  useEffect(() => {
    return safeRequestAnimationFrame(
      () => setIsGlassExpanded(false),
      "toolbar-expanding-popover-glass",
    );
  }, []);
  return (
    <ToolbarPopoverMorphSurface
      accessibilityElementsHidden
      collapsedContent={collapsedContent}
      glassExpanded={isGlassExpanded}
      importantForAccessibility="no-hide-descendants"
      morph={morph}
      // Laid out against the trigger, which measured its own window frame as the anchor.
      origin={morph.anchor}
      pointerEvents="none"
    />
  );
}

/**
 * The popover's material. On iOS Liquid Glass it is a single native glass, the same one the
 * toolbar buttons are made of: it shows first with the button's own tint, so the button can hand
 * over to it without a seam, and then UIKit animates the tint away. What is left is exactly the
 * system menu's material (`MenuPopoverSurface`): an untinted regular glass, which UIKit blurs,
 * bleeds and shadows more as it grows, so labels underneath frost past legibility as they do
 * under a `UIMenu`. Elsewhere a thick blur inside a hairline border stands in.
 */
function ToolbarPopoverSurface({
  expanded,
  style,
}: {
  expanded: boolean;
  style: ComponentProps<typeof Animated.View>["style"];
}) {
  const { theme } = useUniwind();
  const dark = theme === "dark";
  const buttonTint = useToolbarGlassTint();
  if (!isLiquidGlassSupported) {
    return (
      <>
        <MenuPopoverSurface style={style} />
        <Animated.View
          accessibilityElementsHidden
          accessible={false}
          className="absolute inset-0 border border-border"
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
          style={style}
        />
      </>
    );
  }
  return (
    <AnimatedLiquidGlassView
      accessibilityElementsHidden
      accessible={false}
      animationDuration={expanded ? GLASS_EXPAND_MS : GLASS_COLLAPSE_MS}
      className="absolute inset-0"
      // The menu's content takes the theme's inks, so its glass follows the theme too.
      colorScheme={dark ? "dark" : "light"}
      effect="regular"
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      style={style}
      tintColor={expanded ? undefined : buttonTint}
    />
  );
}
